import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail,
  Sparkles,
  Send,
  Save,
  Users,
  Eye,
  Loader2,
  Wand2,
  Trash2,
  History,
  CheckCircle2,
  AlertCircle,
  Edit3,
  Upload,
  Tag,
} from 'lucide-react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import ModalPortal from '../../components/ModalPortal';
import AIImageGenerateModal from '../../components/admin/AIImageGenerateModal';
import ContactsPanel from '../../components/contacts/ContactsPanel';
import ListsPanel from '../../components/contacts/ListsPanel';
import ImportsPanel from '../../components/contacts/ImportsPanel';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'warm and welcoming', label: 'Warm & Welcoming' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'celebratory', label: 'Celebratory' },
  { value: 'informative', label: 'Informative' },
  { value: 'inspiring', label: 'Inspiring' },
];

const LENGTHS = [
  { value: 'short', label: 'Short' },
  { value: 'medium', label: 'Medium' },
  { value: 'long', label: 'Long' },
];

const TABS = [
  { key: 'compose', label: 'Compose', icon: Edit3 },
  { key: 'audience', label: 'Audience', icon: Users },
  { key: 'lists', label: 'Lists & Tags', icon: Tag },
  { key: 'imports', label: 'Imports', icon: Upload },
  { key: 'drafts', label: 'Drafts', icon: Save },
  { key: 'sent', label: 'Sent History', icon: History },
];

const EmailCommunicationsPage = () => {
  const [tab, setTab] = useState('compose');
  const [audiences, setAudiences] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [sentLog, setSentLog] = useState([]);

  // Compose state
  const [draftId, setDraftId] = useState(null);
  const [subject, setSubject] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [audience, setAudience] = useState('all_members');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [aiPrompt, setAiPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [aiImageOpen, setAiImageOpen] = useState(false);
  const [aiImageUrl, setAiImageUrl] = useState('');
  const [audiencePreview, setAudiencePreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  // Audience / Contacts state
  const { user: currentUser } = useAuth();
  const [contactsSummary, setContactsSummary] = useState(null);
  const [contactLists, setContactLists] = useState([]);

  // Lock body scroll while ANY modal is open (preview, send, audience preview)
  useBodyScrollLock(showPreview || showSendModal || !!audiencePreview);
  const loadAudiences = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/comms/audiences`, { withCredentials: true });
      setAudiences(r.data.audiences || []);
    } catch (e) {
      toast.error('Failed to load audiences');
    }
  }, []);

  const loadContactsData = useCallback(async () => {
    try {
      const [sum, lists] = await Promise.all([
        axios.get(`${API}/admin/contacts/summary`, { withCredentials: true }),
        axios.get(`${API}/admin/contact-lists`, { withCredentials: true }),
      ]);
      setContactsSummary(sum.data);
      setContactLists(lists.data?.lists || []);
    } catch (e) { /* silent — the panels will render empty states */ }
  }, []);

  const loadDrafts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/comms/drafts`, { withCredentials: true });
      setDrafts(r.data.items || []);
    } catch (e) { /* silent */ }
  }, []);

  const loadSent = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/comms/sent`, { withCredentials: true });
      setSentLog(r.data.items || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadAudiences();
    loadDrafts();
    loadSent();
    loadContactsData();
  }, [loadAudiences, loadDrafts, loadSent, loadContactsData]);

  const audienceCount = audiences.find((a) => a.key === audience)?.count ?? 0;
  const audienceLabel = audiences.find((a) => a.key === audience)?.label ?? audience;

  // Sync bodyHtml when bodyText changes manually
  useEffect(() => {
    const paragraphs = bodyText.split('\n\n').filter((p) => p.trim());
    const html = paragraphs
      .map((p) => `<p style="margin:0 0 14px;line-height:1.6;color:#1e293b;">${p.replace(/\n/g, '<br/>')}</p>`)
      .join('');
    setBodyHtml(html);
  }, [bodyText]);

  const resetCompose = () => {
    setDraftId(null);
    setSubject('');
    setBodyText('');
    setBodyHtml('');
    setAiPrompt('');
    setAudience('all_members');
    setTone('professional');
    setLength('medium');
  };

  const generateWithAI = async () => {
    if (aiPrompt.trim().length < 5) {
      toast.error('Describe what to write — at least 5 characters');
      return;
    }
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/admin/comms/ai-generate`,
        { prompt: aiPrompt, tone, audience, length },
        { withCredentials: true }
      );
      setSubject(r.data.subject || '');
      setBodyText(r.data.body_text || '');
      toast.success('Draft generated by Claude — review & edit before sending');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'AI generation failed';
      toast.error(typeof msg === 'string' ? msg : 'AI generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const saveDraft = async () => {
    if (!subject.trim() && !bodyText.trim()) {
      toast.error('Nothing to save');
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/comms/drafts`,
        {
          id: draftId || undefined,
          subject,
          body_text: bodyText,
          body_html: bodyHtml,
          audience,
          tone,
          ai_prompt: aiPrompt,
        },
        { withCredentials: true }
      );
      setDraftId(r.data.draft.id);
      toast.success('Draft saved');
      loadDrafts();
    } catch (e) {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const loadDraft = (d) => {
    setDraftId(d.id);
    setSubject(d.subject || '');
    setBodyText(d.body_text || '');
    setBodyHtml(d.body_html || '');
    setAudience(d.audience || 'all_members');
    setTone(d.tone || 'professional');
    setAiPrompt(d.ai_prompt || '');
    setTab('compose');
    toast.info('Draft loaded into composer');
  };

  const deleteDraft = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await axios.delete(`${API}/admin/comms/drafts/${id}`, { withCredentials: true });
      toast.success('Draft deleted');
      if (draftId === id) resetCompose();
      loadDrafts();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const previewAudience = async () => {
    try {
      const r = await axios.get(
        `${API}/admin/comms/audience-preview?audience=${audience}&limit=20`,
        { withCredentials: true }
      );
      setAudiencePreview(r.data);
    } catch {
      toast.error('Failed to load audience preview');
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes('@')) {
      toast.error('Enter a valid test email');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/comms/send`,
        { subject, body_html: bodyHtml, audience, test_mode: true, test_email: testEmail },
        { withCredentials: true }
      );
      toast.success(`Test sent to ${testEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send test');
    } finally {
      setSending(false);
    }
  };

  const sendBlast = async () => {
    if (!subject.trim() || bodyText.trim().length < 20) {
      toast.error('Subject and a longer body are required');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/comms/send`,
        { subject, body_html: bodyHtml, audience, test_mode: false },
        { withCredentials: true }
      );
      toast.success(`Sent to ${r.data.sent} recipients${r.data.failed ? ` · ${r.data.failed} failed` : ''}`);
      setShowSendModal(false);
      loadSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-email-comms">
      {/* Header */}
      <div className="mb-6 sm:mb-8 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#002249] text-white flex items-center justify-center flex-shrink-0">
          <Mail className="w-6 h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#E63946]">Communications</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#002249] tracking-tight">Email Communications</h1>
          <p className="text-[13.5px] text-slate-500 mt-1">Compose, generate with AI (Claude Sonnet 4.5), and send to chosen audiences.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto scrollbar-hide">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold whitespace-nowrap transition-colors ${
                active ? 'text-[#002249]' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`comms-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#002249]" />}
            </button>
          );
        })}
      </div>

      {/* Compose tab */}
      {tab === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-5">
            {/* AI Assistant Card */}
            <div className="bg-gradient-to-br from-[#002249] to-[#001530] text-white rounded-2xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-amber-300" />
                <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber-300">AI Writing Assistant</p>
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-white/50">Claude Sonnet 4.5</span>
              </div>
              <h3 className="text-[16px] sm:text-[17px] font-bold mb-3">Describe the email you want, and I'll write it.</h3>
              <textarea
                rows={3}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="e.g., Announce our annual gala on June 15 and invite RSVPs"
                className="w-full px-3 py-2.5 rounded-lg bg-white/10 border border-white/20 placeholder-white/40 text-white text-[14px] outline-none focus:bg-white/15 focus:border-white/40 resize-none"
                data-testid="ai-prompt-input"
              />
              <div className="grid grid-cols-2 gap-2 mt-3">
                <select
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white text-[12.5px] outline-none"
                  data-testid="ai-tone"
                >
                  {TONES.map((t) => (
                    <option key={t.value} value={t.value} className="text-slate-900">{t.label}</option>
                  ))}
                </select>
                <select
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  className="h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white text-[12.5px] outline-none"
                  data-testid="ai-length"
                >
                  {LENGTHS.map((l) => (
                    <option key={l.value} value={l.value} className="text-slate-900">{l.label}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={generateWithAI}
                disabled={generating}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-[#E63946] hover:bg-[#c02a35] disabled:opacity-60 text-white text-[13.5px] font-semibold transition-colors"
                data-testid="ai-generate-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {generating ? 'Generating with Claude…' : 'Generate Draft'}
              </button>
              <button
                onClick={() => setAiImageOpen(true)}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-[12.5px] font-semibold transition-colors"
                data-testid="ai-image-open-campaign"
              >
                <Sparkles className="w-3.5 h-3.5" /> Generate campaign image with AI
              </button>
              {aiImageUrl && (
                <div className="mt-3 rounded-lg overflow-hidden border border-white/20" data-testid="campaign-ai-image-preview">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${aiImageUrl}`} alt="AI generated campaign hero" className="w-full h-auto" />
                  <div className="flex items-center justify-between text-[11px] px-2 py-1.5 bg-white/5">
                    <span className="opacity-70">Attached — paste into email body or newsletter.</span>
                    <button onClick={() => setAiImageUrl('')} className="underline hover:text-rose-300" data-testid="campaign-ai-image-clear">Remove</button>
                  </div>
                </div>
              )}
            </div>

            {/* Subject + Body */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4">
              <div>
                <label className="text-[12px] font-semibold text-slate-700 mb-1.5 block">Subject Line</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                  placeholder="A clear, engaging subject…"
                  className="w-full h-11 px-3 rounded-lg border border-slate-200 focus:border-[#002249] focus:ring-2 focus:ring-[#002249]/15 outline-none text-[14px]"
                  data-testid="email-subject"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[12px] font-semibold text-slate-700">Email Body</label>
                  <button
                    onClick={() => setShowPreview(true)}
                    disabled={!bodyText.trim()}
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#002249] hover:underline disabled:opacity-40 disabled:no-underline"
                    data-testid="preview-btn"
                  >
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </button>
                </div>
                <textarea
                  rows={14}
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder="Write or generate your message. Separate paragraphs with a blank line."
                  className="w-full px-3 py-3 rounded-lg border border-slate-200 focus:border-[#002249] focus:ring-2 focus:ring-[#002249]/15 outline-none text-[14px] leading-relaxed font-mono"
                  data-testid="email-body"
                />
                <p className="text-[11px] text-slate-400 mt-1">{bodyText.length} characters</p>
              </div>
            </div>
          </div>

          {/* Right rail */}
          <div className="space-y-4">
            {/* Audience */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#E63946] mb-2">Audience</p>
              <div className="space-y-1.5" data-testid="audience-list">
                {audiences.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => setAudience(a.key)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center justify-between transition-colors ${
                      audience === a.key
                        ? 'bg-[#002249] text-white'
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                    }`}
                    data-testid={`audience-${a.key}`}
                  >
                    <span className="text-[13px] font-semibold">{a.label}</span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                      audience === a.key ? 'bg-white/20' : 'bg-white text-slate-500'
                    }`}>
                      {a.count}
                    </span>
                  </button>
                ))}
              </div>
              <button
                onClick={previewAudience}
                className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-[#002249] hover:underline"
                data-testid="audience-preview-btn"
              >
                <Users className="w-3.5 h-3.5" /> Preview recipients
              </button>
            </div>

            {/* Actions */}
            <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-2">
              <button
                onClick={saveDraft}
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-[#002249] text-[13.5px] font-semibold disabled:opacity-60"
                data-testid="save-draft-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {draftId ? 'Update Draft' : 'Save Draft'}
              </button>
              <button
                onClick={() => setShowSendModal(true)}
                disabled={!subject.trim() || bodyText.trim().length < 20}
                className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg bg-[#002249] hover:bg-[#001530] disabled:opacity-50 text-white text-[13.5px] font-semibold transition-colors"
                data-testid="send-btn"
              >
                <Send className="w-4 h-4" />
                Review & Send
              </button>
              {draftId && (
                <button
                  onClick={resetCompose}
                  className="w-full text-[11.5px] text-slate-400 hover:text-slate-600 mt-1"
                >
                  + Start a new email
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Audience / Contacts tab */}
      {tab === 'audience' && (
        <ContactsPanel
          summary={contactsSummary}
          lists={contactLists}
          onReload={loadContactsData}
        />
      )}

      {/* Lists & Tags tab */}
      {tab === 'lists' && (
        <ListsPanel
          lists={contactLists}
          onReload={loadContactsData}
          canDelete={currentUser?.role === 'super_admin'}
        />
      )}

      {/* Imports tab */}
      {tab === 'imports' && (
        <ImportsPanel
          lists={contactLists}
          onReload={loadContactsData}
          canRollback={currentUser?.role === 'super_admin'}
        />
      )}

      {/* Drafts tab */}
      {tab === 'drafts' && (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="drafts-list">
          {drafts.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-[13px]">No saved drafts yet.</div>
          ) : (
            drafts.map((d) => (
              <div key={d.id} className="p-4 sm:p-5 flex items-start gap-4 hover:bg-slate-50/50">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-semibold text-[#002249] truncate">{d.subject || '(no subject)'}</p>
                  <p className="text-[12.5px] text-slate-500 mt-0.5 line-clamp-2">{d.body_text?.slice(0, 200)}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-slate-400">
                    <span className="px-2 py-0.5 bg-slate-100 rounded-full font-semibold">{d.audience}</span>
                    <span>· Updated {new Date(d.updated_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => loadDraft(d)}
                    className="h-9 w-9 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#002249] flex items-center justify-center"
                    title="Edit"
                    data-testid={`draft-edit-${d.id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteDraft(d.id)}
                    className="h-9 w-9 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center"
                    title="Delete"
                    data-testid={`draft-delete-${d.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Sent tab */}
      {tab === 'sent' && (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="sent-list">
          {sentLog.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-[13px]">No emails sent yet.</div>
          ) : (
            sentLog.map((s) => (
              <div key={s.id} className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-semibold text-[#002249] truncate">{s.subject}</p>
                    <p className="text-[12px] text-slate-500 mt-0.5">
                      Sent to <strong>{s.recipient_count}</strong> · {s.audience_label || s.audience}
                      {s.failed_count ? ` · ${s.failed_count} failed` : ''}
                    </p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {new Date(s.sent_at).toLocaleString()} · by {s.created_by_name || 'admin'}
                    </p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Preview modal */}
      <ModalPortal>
        {showPreview && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 bg-black/55 backdrop-blur-sm"
            onClick={() => setShowPreview(false)}
          >
            <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
              <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between">
                <p className="text-[13px] font-semibold text-[#002249]">Preview · How members will see it</p>
                <button onClick={() => setShowPreview(false)} className="text-slate-400 hover:text-slate-700 text-[12px]">Close</button>
              </div>
              <div className="p-5 sm:p-6">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Subject</p>
                <p className="text-[15px] font-semibold text-[#002249] mb-4">{subject || '(no subject)'}</p>
                <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/40">
                  <div dangerouslySetInnerHTML={{ __html: bodyHtml || '<p class="text-slate-400">Empty</p>' }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalPortal>

      {/* Audience preview modal */}
      <ModalPortal>
        {audiencePreview && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 bg-black/55 backdrop-blur-sm"
            onClick={() => setAudiencePreview(null)}
          >
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="audience-preview-modal">
              <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-3 flex items-center justify-between">
                <p className="text-[13px] font-semibold text-[#002249]">{audiencePreview.label} · {audiencePreview.count}</p>
                <button onClick={() => setAudiencePreview(null)} className="text-slate-400 hover:text-slate-700 text-[12px]">Close</button>
              </div>
              <div className="p-5 space-y-1">
                {(audiencePreview.sample || []).map((u, i) => (
                  <div key={i} className="text-[12.5px] text-slate-700 flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
                    <span className="font-semibold truncate">{u.display_name || u.name || '—'}</span>
                    <span className="text-slate-400 truncate ml-3">{u.email}</span>
                  </div>
                ))}
                {audiencePreview.count > (audiencePreview.sample || []).length && (
                  <p className="text-[11.5px] text-slate-400 pt-2">…and {audiencePreview.count - audiencePreview.sample.length} more</p>
                )}
              </div>
            </div>
          </div>
        )}
      </ModalPortal>

      {/* Send confirmation modal */}
      <ModalPortal>
        {showSendModal && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6 bg-black/55 backdrop-blur-sm"
            onClick={() => !sending && setShowSendModal(false)}
          >
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="send-modal">
              <div className="px-6 py-5 border-b border-slate-100">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#E63946]">Confirm Send</p>
                <h3 className="text-[18px] font-bold text-[#002249] mt-1">Send this email?</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <div className="flex items-start gap-2 text-[12.5px]">
                    <span className="text-slate-400 w-20 flex-shrink-0">Subject</span>
                    <span className="font-semibold text-[#002249]">{subject}</span>
                  </div>
                  <div className="flex items-start gap-2 text-[12.5px]">
                    <span className="text-slate-400 w-20 flex-shrink-0">Audience</span>
                    <span className="font-semibold text-[#002249]">{audienceLabel}</span>
                  </div>
                  <div className="flex items-start gap-2 text-[12.5px]">
                    <span className="text-slate-400 w-20 flex-shrink-0">Recipients</span>
                    <span className="font-bold text-[#E63946]">{audienceCount} members</span>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <p className="text-[12px] font-semibold text-slate-700 mb-2">Send a test first (recommended)</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="your-email@example.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="flex-1 h-10 px-3 rounded-lg border border-slate-200 focus:border-[#002249] focus:ring-2 focus:ring-[#002249]/15 outline-none text-[13px]"
                      data-testid="test-email-input"
                    />
                    <button
                      onClick={sendTest}
                      disabled={sending || !testEmail}
                      className="inline-flex items-center gap-1 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-[#002249] text-[12.5px] font-semibold disabled:opacity-50"
                      data-testid="send-test-btn"
                    >
                      {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      Test
                    </button>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[12px] text-amber-800">This will email <strong>{audienceCount}</strong> recipients. This action cannot be undone.</p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <button
                  onClick={() => setShowSendModal(false)}
                  disabled={sending}
                  className="text-[13px] font-semibold text-slate-500 hover:text-slate-700"
                  data-testid="send-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={sendBlast}
                  disabled={sending}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-[#E63946] hover:bg-[#c02a35] disabled:opacity-60 text-white text-[13.5px] font-semibold transition-colors"
                  data-testid="send-confirm-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? 'Sending…' : `Send to ${audienceCount}`}
                </button>
              </div>
            </div>
          </div>
        )}
      </ModalPortal>

      {aiImageOpen && (
        <AIImageGenerateModal
          contextType="campaign"
          contextId={draftId}
          contextData={{
            subject,
            body_text: bodyText,
            body_html: bodyHtml,
          }}
          contextLabel={subject || aiPrompt?.slice(0, 60) || 'New campaign'}
          existingImageUrl={aiImageUrl || null}
          freePromptHint={aiPrompt}
          onImageSelected={(url) => {
            setAiImageUrl(url);
            toast.success('AI image generated — attached to this campaign');
          }}
          onClose={() => setAiImageOpen(false)}
        />
      )}
    </div>
  );
};

export default EmailCommunicationsPage;
